import Button from "../Assets/Button/Button"
import clsx from "clsx"
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { IEmployee } from "../../types/content"
import { useLazyGetDirectoryQuery } from "../../redux/api/content"
import { useAppSelector } from "../../hooks"
import { selectUser } from "../../redux/slice/auth"
import Search from "../Search/Search"
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll"
import ScrollBlock from "../Assets/ScrollBlock"
import { useTranslation } from "react-i18next"
import Employee from "../Employees/Employee/Employee"
import { CheckIcon } from "../../icons/CheckIcon"
import _debounce from "lodash/debounce"
import { TDirectory } from "../../types/orderTypes"
import useWindowSize from "../../hooks/useWindowSize"
import styles from "./SelectReference.module.scss"
import DirectoryObject from "../DirectoryObject/DirectoryObject"

interface IBaseItem {
  id: string
  name: string
}

interface Props {
  type: TDirectory
  onSubmit: (data: { id: string; name: string; avatar_id?: string }[]) => void
  onClose?: () => void
  list?: { id: string; name: string; avatar_id?: string }[]
  isTop?: boolean
  isMultiple?: boolean
  className?: string
  selectUserBtnRef?: RefObject<HTMLDivElement>
  optionId?: string
  projectID: string
}

const LIMIT_ITEMS = 20

const ListLoader = ({ type }: { type?: TDirectory }) => {
  return (
    <div className={clsx("employees__grid", styles.grid, styles["grid--is-skeleton"])}>
      {[...Array(4)].map((_item, index) => (
        <div
          key={index}
          className={clsx(
            styles.skeleton,
            styles["skeleton--sm"],
            "skeletonBlock",
            type === "objects" && styles["skeleton--xl"],
          )}
        />
      ))}
    </div>
  )
}

const SelectReference = ({
  onSubmit,
  onClose,
  list,
  type,
  isTop,
  isMultiple,
  className,
  selectUserBtnRef,
  optionId,
  projectID,
}: Props) => {
  const selectReferenceRef = useRef<HTMLDivElement>(null)
  const searchReferenceRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation("translation", { keyPrefix: `interface` })
  const { isDesktop } = useWindowSize()

  const rect = selectUserBtnRef?.current?.getBoundingClientRect()

  useEffect(() => {
    if (isDesktop) {
      const { current: selectUserBtn } = selectUserBtnRef || { current: null }
      const { current: selectReference } = selectReferenceRef || { current: null }
      const userBtnOffsetTop = selectUserBtn?.offsetTop ?? 0
      const userBtnHeight = selectUserBtn?.clientHeight ?? 0
      const referenceHeight = selectReference?.clientHeight ?? 0

      const left = selectUserBtn?.offsetLeft ?? 0
      let top = userBtnOffsetTop + (!isTop ? userBtnHeight : 0)

      top += isTop ? -referenceHeight - 6 : 6

      if (selectReference) {
        Object.assign(selectReference.style, {
          left: `${left}px`,
          top: `${top}px`,
        })
      }
    }
  }, [rect, isTop])

  const [getDirectory, { isFetching, isLoading, isUninitialized }] = useLazyGetDirectoryQuery()

  const user = useAppSelector(selectUser)

  const [dataState, setDataState] = useState<IBaseItem[]>([])
  const [searchChat, setSearchChat] = useState<string>("")
  const [items, setItems] = useState<IEmployee[] | any[]>([])
  const [offset, setOffset] = useState<number>(0)
  const loaderRef = useRef<HTMLDivElement>(null)
  const [isSearchLoading, setSearchLoading] = useState<boolean>(false)
  const [isAllowFetched, setAllowFetched] = useState<boolean>(false)
  const [isListEnd, setListEnd] = useState<boolean>(false)
  const abortControllerRef = useRef<any>(null)

  const infiniteScroll = useInfiniteScroll<HTMLDivElement>(
    async () => {
      if (isListEnd || isFetching) return
      await new Promise((resolve) => {
        setTimeout(resolve, 1000)
        void fetchData(false)
      })
    },
    { distance: 10, isScrollPlugin: true },
  )

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    // if (type === "users") {
    getDirectory({ type: type, limit: LIMIT_ITEMS, optionId: optionId, project_id: projectID }).then((res) => {
      if (res?.data) {
        setItems(() => res.data?.aObjects || [])
        setListEnd(res.data.bIsEnd)
        setAllowFetched(true)
      }
    })
    // }
  }, [type])

  const fetchData = async (isStart?: boolean) => {
    if (isListEnd) return
    if (isFetching || !isAllowFetched) return
    setOffset((prev) => (isStart ? 0 : prev + LIMIT_ITEMS))
    setAllowFetched(false)
    await getDirectory({
      type: type,
      limit: LIMIT_ITEMS,
      search: searchChat,
      offset: isStart ? 0 : offset,
      optionId: optionId,
      project_id: projectID,
    }).then(({ data }) => {
      if (data?.aObjects) {
        setItems((prevItems) => {
          return !isStart && offset ? [...prevItems, ...data.aObjects] : data?.aObjects || []
        })
        setListEnd(data.bIsEnd)
      }
    })
    setSearchLoading(false)
    setTimeout(() => {
      setAllowFetched(true)
    }, 0)
  }

  useEffect(() => {
    if (!list?.length) return
    setDataState(list)
  }, [list])

  const handleSubmit = () => {
    onSubmit(dataState)
  }

  const handleReset = () => {
    !isDesktop && onClose ? onClose() : setDataState([])
  }

  const dataList = useMemo(() => {
    if (!items?.length) return null
    return items.map((el) => {
      // if (el.id === user?.id) return null
      const isChecked = dataState.some((state) => state.id === el.id)

      return (
        <label key={el.id} className={styles.label}>
          {type === "users" && (
            <Employee {...el} highlightTxt={searchChat} className={clsx(styles.item, className)} size={"sm"} />
          )}
          {type === "objects" && (
            <DirectoryObject {...el} path={el?.parentPath ?? el.path_name} searchStr={searchChat} />
          )}

          {type !== "objects" && type !== "users" && (
            <Employee
              {...el}
              highlightTxt={searchChat}
              className={clsx(styles.item, className)}
              size={"sm"}
              noEmployDirect
            />
          )}

          <div className={styles.inputWrap}>
            <input
              type={isMultiple ? "checkbox" : "radio"}
              hidden
              name={"radio-" + el.id}
              checked={isChecked}
              readOnly
              onClick={() => {
                const objData = Object.keys(el).includes("path_name")
                  ? { id: el.id, name: el.name, path_name: el?.parentPath ?? el.path_name }
                  : { id: el.id, name: el.name, avatar_id: el?.avatar_id }
                setDataState((prev) => {
                  if (!isMultiple) return [objData]
                  return isChecked ? prev.filter((prevItem: any) => prevItem.id !== el.id) : [...prev, objData]
                })
              }}
            />
            {isChecked && <CheckIcon />}
            <div className={styles.itemIco} />
          </div>
        </label>
      )
    })
  }, [items, searchChat, user?.id, isMultiple, dataState])

  useEffect(() => {
    if (isUninitialized) return
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0]
      if (target.isIntersecting) {
        if (isFetching || isSearchLoading || !isAllowFetched) return
        void fetchData(false)
      }
    })

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current)
        observer.disconnect()
      }
    }
  }, [isUninitialized, isFetching, isLoading, isSearchLoading, isAllowFetched, dataList])

  const debounceFn = useCallback(
    _debounce((str: string) => {
      abortControllerRef.current = new AbortController()
      if (isUninitialized) return
      if (!isAllowFetched) return
      // if (isSearchLoading) return
      setSearchLoading(true)
      getDirectory({
        type: type,
        limit: LIMIT_ITEMS,
        search: str,
        offset: 0,
        optionId: optionId,
        project_id: projectID,
        signal: abortControllerRef?.current?.signal,
      }).then(({ data }) => {
        if (data?.aObjects) {
          setItems(() => data?.aObjects || [])
          setSearchLoading(false)
          setListEnd(data.bIsEnd)
          setTimeout(() => {
            setAllowFetched(true)
          }, 0)
        }
      })
    }, 500),
    [isUninitialized, isAllowFetched, isSearchLoading, type, abortControllerRef?.current],
  )

  useEffect(() => {
    abortControllerRef?.current?.abort()
    abortControllerRef.current = null
    setOffset(0)
    debounceFn(searchChat)
  }, [searchChat])

  useEffect(() => {
    if (!isLoading) {
      if (!isDesktop && searchReferenceRef.current) {
        searchReferenceRef.current.scrollIntoView({ block: "center", behavior: "smooth" })
      }
    }
  }, [selectReferenceRef.current?.clientHeight])

  return (
    <div
      className={clsx(
        styles.wrap,
        isTop && styles["wrap--is-top"],
        className,
        type === "objects" && styles["wrap-is-objects"],
      )}
      ref={selectReferenceRef}
    >
      <div ref={searchReferenceRef}>
        <Search searchVal={searchChat} setSearchVal={setSearchChat} className={styles.search} />
      </div>

      <ScrollBlock
        //@ts-ignore
        ref={infiniteScroll.ref}
        hideTracksWhenNotNeeded
        scrollBarSize={4}
        autoHeightMax={isDesktop ? (type === "objects" ? 254 : 145) : 340}
      >
        {!isUninitialized && !isSearchLoading && (
          <>
            {dataList && dataList?.length > 0 ? (
              <div className={clsx("employees__grid", styles.grid)}>{dataList}</div>
            ) : (
              <>
                {!isSearchLoading && !isFetching && !isLoading && (
                  <div className={styles.nothing}>{t("objNotFound")}</div>
                )}
              </>
            )}
          </>
        )}

        {dataList?.length && (infiniteScroll.isLoading || isFetching) && <ListLoader type={type} />}
        {isFetching && !dataList?.length && <ListLoader type={type} />}
      </ScrollBlock>

      <footer className={styles.footer}>
        <Button
          txt={t(isDesktop ? "reset" : "back")}
          mode={"gray"}
          size={"xsm"}
          className={styles.btnReset}
          onClick={handleReset}
        />
        <Button txt={t("apply")} size={"xsm"} className={styles.btnSubmit} onClick={handleSubmit} />
      </footer>
    </div>
  )
}
export default SelectReference
